import { ReactElement, Suspense, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import {
  competitions,
  gameSportcastId,
  regions,
  sports
} from 'utils/constants/sportsbook/data-fields';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy as lazyImport } from 'utils/generic/lazy';
import RidGenerator from 'utils/swarm/rid-generator';
import { ENotRealSports } from 'interfaces/not-real-sports';
import { Sport } from 'interfaces/sportsbook-data-levels';
import { executeCommand, unsubscribeCommand } from 'services/get-data-level';
import {
  createGetSportDataWhereObj,
  TPrematchCategoryTypes
} from 'newelements/Sportsbook/helpers';
import { NoItem } from 'components/Shared/NoItem';
import { RegionSkeleton } from 'components/UI/Skeletons/RegionSkeleton';
import { NavigationCollapseWrapper } from 'newcomponents/Shared/sportsbook/NavigationCollapseWrapper';
import { useGetUserSportOrder } from 'hooks/sportsbook/useGetUserSportOrder';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useMounted } from 'hooks/useMounted';
import {
  setShowNoItemWrapper,
  setSwarmResponse
} from 'store/actions/new-sportsbook';
import {
  getSwarmResponse,
  getUserSportOrder
} from 'store/selectors/new-sportsbook';
import {
  getBoostedOddsIds,
  getMultipleSelectionsCompIds,
  getTimeFiltersData
} from 'store/selectors/sport-data';

const { CouponLevelNavigation } = lazyImport(
  () =>
    import(
      /* webpackChunkName: "coupon-level-navigation" */ 'newcomponents/Shared/sportsbook/NavigationCollapseWrapper/CouponLevelNavigation'
    )
);

export const CategoriesNavigation = ({
  category
}: {
  category: TPrematchCategoryTypes;
}): ReactElement => {
  const configs = useSportsbookConfigs();

  const dispatch = useDispatch();
  const { mounted } = useMounted();
  const { t } = useTranslation();

  const timeStamp = useSelector(getTimeFiltersData);
  const categoryData = useSelector(getSwarmResponse)?.[category];
  const multipleSelectionsIds = useSelector(getMultipleSelectionsCompIds);
  const boostedOddsIds = useSelector(getBoostedOddsIds);
  const { orderSports, orderIsLoading } = useGetUserSportOrder();
  const userSportOrder = useSelector(getUserSportOrder);

  useEffect(() => {
    let shouldUnsubscribe = false;

    if (!category) {
      return;
    }

    if (category === ENotRealSports.boostedOdds && !boostedOddsIds) {
      return;
    } else if (
      category === ENotRealSports.multiples &&
      !multipleSelectionsIds
    ) {
      return;
    }

    if (
      category === ENotRealSports.multiples &&
      multipleSelectionsIds?.size === 0
    ) {
      dispatch(setSwarmResponse({ [category]: {} }));
    }

    shouldUnsubscribe = true;

    const rid = RidGenerator.gForSubscribe(`Prematch_${category}`);

    const where = createGetSportDataWhereObj(
      '',
      category,
      configs.sportType,
      Array.from(multipleSelectionsIds || []),
      boostedOddsIds
    );

    const relations: Record<string, any> = {
      ...regions,
      ...competitions,
      ...sports
    };

    if (category === ENotRealSports.topLeague) {
      relations.competition.push('favorite_order');
    } else if (category === ENotRealSports.betBuilder) {
      relations.game = gameSportcastId;
    }

    if (
      (category || SpringConfigs.MOCKED_DATA) &&
      category !== ENotRealSports.coupon
    ) {
      const handleResponseSportsData = (data: {
        sport: Record<string, Sport>;
      }) => {
        if (mounted.current && data.sport) {
          dispatch(setSwarmResponse({ [category]: data.sport }));
        }

        if (!Object.keys(data.sport).length) {
          dispatch(
            setShowNoItemWrapper({
              game: true,
              competitions: true,
              regions: true
            })
          );
        }
      };

      executeCommand(
        rid,
        relations,
        where,
        handleResponseSportsData,
        true,
        handleResponseSportsData
      );
    }

    return () => {
      if (shouldUnsubscribe) {
        unsubscribeCommand(rid);
      }
    };
  }, [category, boostedOddsIds, multipleSelectionsIds, timeStamp]);

  const orderedData = useMemo(() => {
    return categoryData && sortByOrder(Object.values(categoryData));
  }, [categoryData]);

  useEffect(() => {
    if (
      (category === ENotRealSports.topLeague ||
        category === ENotRealSports.topMatches ||
        category === ENotRealSports.olympicCompetition) &&
      categoryData &&
      !Object.values(categoryData).length
    ) {
      dispatch(setShowNoItemWrapper({ competitions: true, game: true }));
    }
  }, [categoryData, category]);

  const orderedSportData = useMemo(() => {
    return orderedData
      ? orderSports(JSON.parse(JSON.stringify(orderedData)))
      : orderedData;
  }, [userSportOrder, orderedData]);

  if (orderedSportData && Object.keys(orderedSportData).length === 0) {
    if (ENotRealSports.multiples) {
      return (
        <NoItem
          title={t('sportsbook.noGame')}
          text={t('sportsbook.noGameText')}
          size="medium"
        />
      );
    }

    return <NoItem title={t('sportsbook.noCompetition')} />;
  }

  if (orderedData && !orderIsLoading) {
    return (
      <NavigationCollapseWrapper
        navigationData={orderedSportData as Sport[]}
        levelAlias={category}
        sportLevel
      />
    );
  }

  if (category === ENotRealSports.coupon) {
    return (
      <Suspense fallback={<RegionSkeleton />}>
        <CouponLevelNavigation />
      </Suspense>
    );
  }

  return <RegionSkeleton />;
};
